import {
  DetailPage,
  DetailPageTopper,
} from "design-system/components/pages/detail-page/detail-page";
import { Aside } from "design-system/components/primitives/aside/aside";
import { Footnotes } from "design-system/components/primitives/footnote/footnotes";
import { SiblingNavigation } from "design-system/components/primitives/sibling-navigation/sibling-navigation";
import { ProgramComparisonProvider } from "design-system/hooks/use-program-comparison";
import { ProgramComparisonTray } from "design-system/sites/exed/program-comparison-tray/program-comparison-tray";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { DetailPageEntry } from "frontend/contentful/schema/pages";
import { useContentful } from "frontend/hooks/use-contentful";
import { usePageFootnotes } from "frontend/hooks/use-page-footnotes";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { usePageProps } from "frontend/hooks/use-page-props";
import { checkNeedsProgramComparison } from "frontend/lib/needs-program-comparison";
import { ContentfulHead } from "../primitives/head.contentful";
import { createLazyTopperProps } from "../toppers/lazy-topper.props";
import { createSiteFooterProps } from "../universal/site-footer.props";
import { createSiteHeaderProps } from "../universal/site-header.props";
import { RenderContentfulEntry } from "../utils/render-contentful-entry";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { ContentfulPageSections } from "./page-sections.contentful";
import {
  ContentReferencesEntry,
  OnlineContentReferencesEntry,
} from "frontend/contentful/schema/universal";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { EntryRef } from "frontend/contentful/schema/sys";
import { LinkEntry } from "frontend/contentful/schema/primitives";
import { getFaqStructuredData } from "../utils/get-faq-structured-data";
import { AccordionEntry } from "frontend/contentful/schema/blocks";
import { getBreadcrumbStructuredData } from "../utils/get-breadcrumb-structured-data";

export const ContentfulDetailPage = createContentfulComponent<DetailPageEntry>(
  ({ entry }) => {
    const { siteDetailPageSettings, includes } = usePageProps();
    const { site, getEntry, getEntryUrl } = useContentful();
    const { navigation, breadcrumbs } = usePageNavigation();
    const footnotes = usePageFootnotes(entry.fields.blocks);
    const hasRightRail = entry.fields.rightRail;
    const needsProgramComparison = checkNeedsProgramComparison(includes);

    const siteContentReferences = getEntry(site?.fields.contentReferences);
    let finderPageRef: EntryRef<LinkEntry> | undefined = undefined;
    if (
      isContentType<ContentReferencesEntry>(
        "contentReferences",
        siteContentReferences,
      )
    ) {
      finderPageRef = siteContentReferences?.fields.programFinderPage;
    } else if (
      isContentType<OnlineContentReferencesEntry>(
        "onlineContentReferences",
        siteContentReferences,
      )
    ) {
      finderPageRef = siteContentReferences?.fields.courseFinderPage;
    }

    let finderUrl: string | undefined;

    if (finderPageRef) {
      finderUrl = getEntryUrl(finderPageRef);
    }

    // If we open this up to other sites other then ExEd, we need to re-review and adjust settings to be more dynamic as needed
    let faqStructuredData: ReturnType<typeof getFaqStructuredData> | undefined =
      undefined;
    let breadcrumbStructuredData:
      | ReturnType<typeof getBreadcrumbStructuredData>
      | undefined = undefined;

    if (site?.fields.title === "Executive Education") {
      if (breadcrumbs.breadcrumbs && breadcrumbs.breadcrumbs.length > 0) {
        breadcrumbStructuredData = getBreadcrumbStructuredData(
          breadcrumbs,
          site,
        );
      }

      if (entry.fields.blocks) {
        // Find if any of the blocks is an Accordion and assume its an FAQ
        entry.fields.blocks.forEach((block) => {
          const blockEntry = getEntry(block);
          if (
            blockEntry &&
            isContentType<AccordionEntry>("accordion", blockEntry)
          ) {
            if (blockEntry.fields.faqSchema) {
              faqStructuredData = getFaqStructuredData(blockEntry);
            }
          }
        });
      }
    }

    return (
      <>
        <ContentfulHead
          entry={entry.fields.seo}
          breadcrumbStructuredData={breadcrumbStructuredData}
          faqPageStructuredData={faqStructuredData}
        />

        <DetailPage
          theme={getEntry(siteDetailPageSettings)?.fields.colorThemeOverride}
          breadcrumbs={breadcrumbs}
          topper={
            createLazyTopperProps(entry.fields.topper) as DetailPageTopper
          }
          header={{ name: "SiteHeader", props: createSiteHeaderProps(site) }}
          footer={createSiteFooterProps(site?.fields.footer)}
          body={{
            children: needsProgramComparison ? (
              <>
                <ProgramComparisonProvider programs={[]}>
                  {entry.fields.blocks?.map((block, i) => (
                    <RenderContentfulEntry key={i} entry={block} />
                  ))}
                  {<Footnotes footnotes={footnotes} />}
                  <ProgramComparisonTray
                    contentReferences={siteContentReferences}
                    isHBSOnline={site?.fields.domain === "online"}
                    programFinderUrl={finderUrl}
                  />
                </ProgramComparisonProvider>
              </>
            ) : (
              <>
                {entry.fields.blocks?.map((block, i) => (
                  <RenderContentfulEntry key={i} entry={block} />
                ))}
                <Footnotes footnotes={footnotes} />
              </>
            ),
            leftRail:
              navigation.menu.length > 0 ? (
                <Aside>
                  <SiblingNavigation {...navigation} />
                </Aside>
              ) : null,
            preFooterContent: (
              <ContentfulPageSections
                entry={entry}
                options={{ key: "preFooterContent" }}
              />
            ),
            rightRail: hasRightRail ? (
              <Aside>
                <RenderContentfulRichText document={entry.fields.rightRail} />
              </Aside>
            ) : null,
          }}
        />
      </>
    );
  },
);
